import React, { useState, useEffect } from 'react';
import Card from '../../../../shared/components/Card';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export interface WallThread {
  id: string;
  title: string;
  isImportant?: boolean;
  visits: number;
  contributions: number;
}

export interface WallMessage {
  id: string;
  author: string;
  content: string;
  date: string;
}

const MOCK_THREADS: WallThread[] = [
  { id: '1', title: "IMPORTANT = Règlement de l'alliance & Lien Discord", isImportant: true, visits: 7842, contributions: 1 },
  { id: '2', title: 'Demande de formation', visits: 31777, contributions: 12 },
  { id: '3', title: 'X COTISATION X Suppressions des comptes suivants', visits: 51, contributions: 2 },
  { id: '4', title: 'Demande de bâtiment', visits: 2797, contributions: 2 },
  { id: '5', title: '? Aide en jeu', visits: 14410, contributions: 159 },
  { id: '6', title: '! Gagner des € (partages)', visits: 834, contributions: 1 },
  { id: '7', title: 'Infos & Tutos', visits: 5587, contributions: 7 },
];

const MOCK_MESSAGES: Record<string, WallMessage[]> = {
  '1': [
    { id: 'm1', author: 'Admin', content: "Bienvenue ! Voici le règlement de l'alliance et le lien Discord : discord.gg/...", date: '2024-01-15 14:00' },
  ],
  '2': [
    { id: 'm2a', author: 'Joueur1', content: 'Je souhaite demander une formation secourisme.', date: '2024-01-20 10:30' },
    { id: 'm2b', author: 'Formateur', content: 'Ok, dispo la semaine prochaine. Tu préfères quel jour ?', date: '2024-01-20 11:00' },
  ],
};

const AllianceWall: React.FC = () => {
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WallMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');

  const selectedThread = selectedThreadId ? MOCK_THREADS.find((th) => th.id === selectedThreadId) : null;

  useEffect(() => {
    if (selectedThreadId) {
      setMessages(MOCK_MESSAGES[selectedThreadId] ?? []);
      setNewMessage('');
    }
  }, [selectedThreadId]);

  useEffect(() => {
    if (!selectedThreadId) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedThreadId(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedThreadId]);

  const handleSendMessage = () => {
    const trimmed = newMessage.trim();
    if (!trimmed || !selectedThreadId) return;
    setMessages((prev) => [
      ...prev,
      { id: `new-${Date.now()}`, author: 'Moi', content: trimmed, date: new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) },
    ]);
    setNewMessage('');
  };

  return (
    <Card className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">{t('wall.title')}</h2>
          <p className="text-sm text-gray-400 mt-1">{t('wall.description')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {t('wall.createPoll')}
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {t('wall.createThread')}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-800/80 border-b border-gray-700">
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('wall.colThread')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300 text-right w-24">{t('wall.colVisits')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300 text-right w-28">{t('wall.colContributions')}</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_THREADS.map((thread) => (
              <tr key={thread.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setSelectedThreadId(thread.id)}
                    className="w-full text-left flex items-center gap-2 py-1 rounded hover:bg-gray-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset"
                  >
                    {thread.isImportant && (
                      <span className="flex items-center justify-center w-6 h-6 rounded bg-yellow-500/20 text-yellow-400 shrink-0">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
                        </svg>
                      </span>
                    )}
                    <span className={`font-medium ${thread.isImportant ? 'text-yellow-400' : 'text-white'}`}>
                      {thread.title}
                    </span>
                  </button>
                </td>
                <td className="px-4 py-3 text-gray-400 text-right tabular-nums">
                  {thread.visits.toLocaleString('fr-FR')}
                </td>
                <td className="px-4 py-3 text-gray-400 text-right tabular-nums">
                  {thread.contributions.toLocaleString('fr-FR')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedThread && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedThreadId(null)} aria-hidden />
          <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700 shrink-0">
              <h3 className="text-lg font-bold text-white pr-4">{selectedThread.title}</h3>
              <button
                type="button"
                onClick={() => setSelectedThreadId(null)}
                className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors shrink-0"
                aria-label={t('wall.close')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
              {messages.length === 0 ? (
                <p className="text-gray-500 text-sm">{t('wall.noMessages')}</p>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className="bg-gray-800/60 rounded-lg p-3">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="font-semibold text-indigo-400">{msg.author}</span>
                      <span className="text-xs text-gray-500">{msg.date}</span>
                    </div>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t border-gray-700 shrink-0 flex gap-2">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('wall.writeMessage')}
                rows={2}
                className="flex-1 min-h-0 px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 resize-y text-sm"
              />
              <button
                type="button"
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium shrink-0 self-end"
              >
                {t('wall.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AllianceWall;
